/**
 * use-join-state.ts — how a way in's join stands, read once for every way in
 * from the auth store, for the card to say (JoinStatus, entry-card.tsx).
 *
 * A way in whose session has not started waits for it (R1): while the server
 * says the session's match is not up yet, it asks again every few seconds,
 * for about a minute, and only then hands Retry to the player. The auth
 * service starts the wait and says when it runs out (the auth store's
 * waitUntil); a way in that waits — the student join and the session link —
 * passes `askAgain`, which is called while it runs. A join the player sends
 * starts a wait afresh.
 */

import { useEffect } from 'react';

import { isWaitingForStart, NOT_STARTED_WAIT, refusalWords, type RefusalWords } from '../../domains/join-refusals';
import { useAuthStore, type ConnectionStep } from '../../domains/auth';
import { useNowSeconds } from '../../utils/use-now-seconds';

/**
 * How a join stands on its card. Before a press, a way in that waits for one
 * is `ready` (the gold that joins, and a line saying what it does), or
 * `checking` its link; from the press — or the page's opening, for a way in
 * that joins at once — the join is under way, waited through, or refused; and
 * after the menu's Disconnect the player has `left`, and the way in waits on
 * Rejoin (S1).
 */
export type JoinState =
  | { kind: 'ready'; label: string; hint?: string }
  | { kind: 'checking'; says: string }
  | { kind: 'joining'; step: ConnectionStep }
  | { kind: 'waiting' }
  | { kind: 'refused'; words: RefusalWords }
  | { kind: 'left' };

/**
 * How the join stands: left, refused, waited through, or under way. With
 * `askAgain`, a session not up yet is waited for, and `askAgain` is called
 * every few seconds while the wait runs, to send the join again as one sent
 * while waiting.
 */
export function useJoinState(askAgain?: () => void): JoinState {
  const step = useAuthStore((s) => s.connectionStep);
  const status = useAuthStore((s) => s.connectionStatus);
  const refusal = useAuthStore((s) => s.refusal);
  const waitUntil = useAuthStore((s) => s.waitUntil);
  const left = useAuthStore((s) => s.leftMatch);
  const waits = askAgain !== undefined;
  // The clock ticks only while a wait can run.
  const now = useNowSeconds(waits && waitUntil !== null);
  const connecting = status === 'connecting';
  const waiting = waits && isWaitingForStart({ refusal, waitUntil, connecting, nowMs: now * 1000 });
  const due = waiting && !connecting;

  useEffect(() => {
    if (!due || !askAgain) return;
    const timer = window.setTimeout(askAgain, NOT_STARTED_WAIT.everyMs);
    return () => window.clearTimeout(timer);
  }, [due, askAgain]);

  if (left) return { kind: 'left' };
  if (waiting) return { kind: 'waiting' };
  if (status === 'error' && refusal !== null) return { kind: 'refused', words: refusalWords(refusal) };
  return { kind: 'joining', step: step ?? 'authenticating' };
}
